import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../../api/api';
import { formatDate } from '../../utils/dateUtils';
import EditLearningPlanModal from '../../components/learning-plans/EditLearningPlanModal';
import '../../styles/CreateAssignmentPage.css';

const LearningPlanDetailsPage = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [plan, setPlan] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [deleteAssignments, setDeleteAssignments] = useState(false);

  const fetchPlanDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/learning-plans/${planId}`);
      const planData = response.data.data || response.data;
      setPlan(planData);

      // Fetch assignments for this plan
      const assignmentsResponse = await api.get(`/learning-plans/${planId}/assignments`);
      setAssignments(assignmentsResponse.data.data || []);
    } catch (error) {
      console.error('Error fetching plan details:', error);
      setError('Failed to load learning plan details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch plan details on mount and handle navigation state
  useEffect(() => {
    fetchPlanDetails();

    // Check for success message from navigation state
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      // Clear the state after showing message
      window.history.replaceState({}, document.title);

      // Auto-clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]); // Only re-run when planId changes

  // Handle status change
  // const handleStatusChange = (newStatus) => {
  //   setPendingStatus(newStatus);
  //   setShowStatusModal(true);
  // };

  const confirmStatusChange = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.patch(`/learning-plans/${planId}/status`, {
        status: pendingStatus
      });

      if (response.data.success) {
        setPlan(prev => ({ ...prev, status: pendingStatus }));
        setShowStatusModal(false);
        setPendingStatus(null);
        setSuccessMessage(`Plan status updated to ${pendingStatus}`);
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (error) {
      console.error('Error updating plan status:', error);
      setError(error.response?.data?.message || 'Failed to update plan status');
    } finally {
      setLoading(false);
    }
  };

  // Handle plan deletion
  const handleDeletePlan = async () => {
    try {
      setLoading(true);
      const response = await api.delete(`/learning-plans/${planId}`, {
        data: { deleteAssignments }
      });

      const message = response.data.message || 'Learning plan deleted successfully';

      navigate('/learning-plans', {
        state: { successMessage: message }
      });
    } catch (error) {
      console.error('Error deleting plan:', error);
      setError(error.response?.data?.message || 'Failed to delete plan');
      setShowDeleteModal(false);
    } finally {
      setLoading(false);
    }
  };

  // Get goal display text
  const getGoalDisplay = (goal) => {
    if (goal.type === 'pages') {
      return {
        title: 'Pages Goal',
        detail: `Pages ${goal.pages.from}-${goal.pages.to}`,
        total: `${goal.pages.total} pages`
      };
    } else if (goal.type === 'verses') {
      // Handle both new range format and legacy format
      const startFrom = goal.verses?.startFrom;
      const endAt = goal.verses?.endAt;
      const total = goal.verses?.total || 0;
      const direction = goal.direction || 'forward';

      let detail = `${total} verses`;
      let rangeInfo = '';

      if (startFrom && endAt) {
        // New range format - use backend-provided surah names from TarteelService
        const startName = startFrom.surahName || `Surah ${startFrom.surah}`;
        const endName = endAt.surahName || `Surah ${endAt.surah}`;
        const arrow = direction === 'backward' ? '←' : '→';
        rangeInfo = `${startName} ${startFrom.verse} ${arrow} ${endName} ${endAt.verse}`;
      } else if (startFrom) {
        // Legacy format
        const startName = startFrom.surahName || `Surah ${startFrom.surah}`;
        rangeInfo = `Starting from ${startName}, Verse ${startFrom.verse}`;
      }

      return {
        title: 'Verses Goal',
        detail,
        total: rangeInfo || 'Range not specified'
      };
    } else if (goal.type === 'juz') {
      return {
        title: 'Juz Goal',
        detail: `Juz ${goal.juz.number}`,
        total: `${goal.juz.versesCount} verses`
      };
    } else if (goal.type === 'surahs') {
      return {
        title: 'Surahs Goal',
        detail: `${goal.surahs.length} surahs`,
        total: goal.surahs.map(s => s.name).join(', ')
      };
    } else if (goal.type === 'lines') {
      const startFrom = goal.lines?.startFrom;
      const dailyLines = goal.lines?.dailyLines || 0;
      const direction = goal.direction || 'forward';

      // Use backend-provided surah name from TarteelService
      const startName = startFrom?.surahName || `Surah ${startFrom?.surah || ''}`;
      const directionText = direction === 'backward' ? ' (Backward ←)' : ' (Forward →)';

      return {
        title: 'Lines Goal',
        detail: `~${dailyLines} lines per day${directionText}`,
        total: `Starting from ${startName}, Verse ${startFrom?.verse || ''}`
      };
    }
    return { title: 'Unknown', detail: '', total: '' };
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return '#4caf50';
      case 'paused':
        return '#ff9800';
      case 'completed':
        return '#2196f3';
      case 'cancelled':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  };

  // Get progress bar color
  const getProgressColor = (percent) => {
    if (percent >= 75) return '#4caf50';
    if (percent >= 50) return '#2196f3';
    if (percent >= 25) return '#ff9800';
    return '#f44336';
  };

  // Get schedule status
  // progress.due now represents OVERDUE assignments (deadline passed, not completed)
  const getScheduleStatus = (plan) => {
    const overdue = plan.progress?.due || 0;

    // No overdue assignments = on track
    if (overdue === 0) return { text: 'On Track', icon: '✓', color: '#4caf50' };

    // Has overdue assignments
    if (overdue >= 3) return { text: 'Behind Schedule', icon: '⚠', color: '#f44336' };
    return { text: 'Slightly Behind', icon: '⚠', color: '#ff9800' };
  };

  // Get assignment content display
  const getAssignmentContent = (assignment) => {
    if (assignment.pages) {
      if (assignment.pages.from === assignment.pages.to) {
        return `Page ${assignment.pages.from}`;
      }
      return `Pages ${assignment.pages.from}-${assignment.pages.to}`;
    }

    if (assignment.range) {
      const { start, end } = assignment.range;
      if (start.surah.id === end.surah.id) {
        return `${start.surah.name} ${start.verse}-${end.verse}`;
      }
      return `${start.surah.name} ${start.verse} to ${end.surah.name} ${end.verse}`;
    }

    if (assignment.surah) {
      return `Surah ${assignment.surah.name}`;
    }

    return assignment.title;
  };

  // Get assignment status badge
  const getAssignmentStatusBadge = (assignment) => {
    // Check if assignment is completed (assessed)
    if (assignment.status === 'completed' || (assignment.assessment && assignment.assessment.rating)) {
      return <span style={{color: '#4caf50', fontSize: '12px', fontWeight: '500'}}>Completed</span>;
    }

    // Check if due today (same day, not past)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(assignment.dueDate);
    dueDate.setHours(0, 0, 0, 0);

    const isDueToday = dueDate.getTime() === today.getTime();
    if (isDueToday) {
      return <span style={{color: '#ff9800', fontSize: '12px', fontWeight: '500'}}>Due Today</span>;
    }

    // Check if past due
    const isPastDue = dueDate < today;
    if (isPastDue) {
      return <span style={{color: '#f44336', fontSize: '12px', fontWeight: '500'}}>Past Due</span>;
    }

    return <span style={{color: '#999', fontSize: '12px'}}>Pending</span>;
  };

  if (loading) {
    return (
      <div className="create-assignment-container">
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
          <p>Loading plan details...</p>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="create-assignment-container">
        <div className="header">
          <h1>Plan Not Found</h1>
          <Link to="/learning-plans" className="back-btn">
            ← Back to Plans
          </Link>
        </div>
        <div className="form-card">
          <div className="form-section" style={{textAlign: 'center', padding: '60px 20px'}}>
            <p style={{color: '#666'}}>The learning plan you're looking for doesn't exist or has been deleted.</p>
          </div>
        </div>
      </div>
    );
  }

  const goalInfo = getGoalDisplay(plan.goal);

  // Calculate average rating for plan assignments (excluding "Need to Repeat")
  const calculatePlanAverageRating = () => {
    const getRatingValue = (rating) => {
      const ratingMap = {
        'Excellent': 5,
        'Very Good': 4,
        'Good': 3,
        'Pass': 2,
        'Weak': 1
      };
      return ratingMap[rating] || 0;
    };

    // Filter for assessed assignments (exclude "Need to Repeat")
    const ratedAssignments = assignments.filter(a =>
      a.assessment &&
      a.assessment.rating &&
      a.assessment.rating !== 'Need to Repeat'
    );

    if (ratedAssignments.length === 0) return null;

    let totalRating = 0;
    ratedAssignments.forEach(assignment => {
      totalRating += getRatingValue(assignment.assessment.rating);
    });

    return {
      average: (totalRating / ratedAssignments.length).toFixed(1),
      count: ratedAssignments.length
    };
  };

  const planRating = calculatePlanAverageRating();

  return (
    <div className="create-assignment-container">
      <div className="header">
        <h1>Learning Plan Details</h1>
        {location.state?.fromStudentProfile ? (
          <button
            onClick={() => navigate(`/students/${location.state.studentId}`, {
              state: {
                studentId: location.state.studentId,
                studentName: location.state.studentName,
                halaqaId: location.state.contextHalaqaId,
                halaqaName: location.state.contextHalaqaName
              }
            })}
            className="back-btn"
          >
            ← Back to {location.state.studentName}'s Profile
          </button>
        ) : (
          <Link to="/learning-plans" className="back-btn">
            ← Back to Plans
          </Link>
        )}
      </div>

      {/* Success Message */}
      {successMessage && (
        <div style={{marginBottom: '20px', padding: '12px', backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '8px', border: '1px solid #4caf50'}}>
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-message" style={{marginBottom: '20px', padding: '12px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '8px'}}>
          {error}
        </div>
      )}

      {/* Plan Summary Card */}
      <div className="form-card" style={{marginBottom: '20px'}}>
        <div className="form-section">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '20px'}}>
            <div>
              <h2 style={{margin: '0 0 10px 0', fontSize: '24px', color: '#333'}}>
                {plan.studentId?.name || 'Student'}'s Learning Plan
              </h2>
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span
                  style={{
                    padding: '6px 14px',
                    backgroundColor: getStatusColor(plan.status),
                    color: 'white',
                    borderRadius: '14px',
                    fontSize: '13px',
                    fontWeight: '500',
                    textTransform: 'capitalize'
                  }}
                >
                  {plan.status}
                </span>
                {planRating && (
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 14px',
                      backgroundColor: '#e3f2fd',
                      borderRadius: '14px',
                      cursor: 'help'
                    }}
                    title={`Based on ${planRating.count} assessed assignment${planRating.count !== 1 ? 's' : ''}`}
                  >
                    <span style={{fontSize: '15px', fontWeight: '600', color: '#1976d2'}}>
                      {planRating.average}/5
                    </span>
                    <span style={{fontSize: '12px', color: '#666'}}>
                      Avg. Rating
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Status Actions */}
            <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end'}}>
              {/* Edit Button - only for active/paused plans */}
              {(plan.status === 'active' || plan.status === 'paused') && (
                <button
                  onClick={() => setShowEditModal(true)}
                  className="btn-secondary"
                  style={{padding: '8px 16px', fontSize: '14px', backgroundColor: '#2196f3', color: 'white', border: 'none'}}
                >
                  ✏️ Edit Plan
                </button>
              )}
              {/* Temporarily hidden during initial launch - will decide what to keep later */}
              {/* {plan.status === 'active' && (
                <button
                  onClick={() => handleStatusChange('paused')}
                  className="btn-secondary"
                  style={{padding: '8px 16px', fontSize: '14px'}}
                >
                  Pause Plan
                </button>
              )}
              {plan.status === 'paused' && (
                <button
                  onClick={() => handleStatusChange('active')}
                  className="btn-primary"
                  style={{padding: '8px 16px', fontSize: '14px'}}
                >
                  Resume Plan
                </button>
              )}
              {(plan.status === 'active' || plan.status === 'paused') && (
                <button
                  onClick={() => handleStatusChange('completed')}
                  className="btn-primary"
                  style={{padding: '8px 16px', fontSize: '14px', backgroundColor: '#2196f3'}}
                >
                  Mark Complete
                </button>
              )} */}
              <button
                onClick={() => setShowDeleteModal(true)}
                className="btn-danger"
                style={{padding: '8px 16px', fontSize: '14px', backgroundColor: '#d32f2f', color: 'white', border: 'none'}}
              >
                Delete Plan
              </button>
            </div>
          </div>

          {/* Goal Information */}
          <div style={{marginBottom: '20px', padding: '15px', backgroundColor: '#f5f7fa', borderRadius: '8px'}}>
            <h3 style={{margin: '0 0 10px 0', fontSize: '16px', color: '#555'}}>{goalInfo.title}</h3>
            <div style={{fontSize: '14px', color: '#666', marginBottom: '5px'}}>
              <strong>{goalInfo.detail}</strong>
            </div>
            <div style={{fontSize: '13px', color: '#888'}}>
              {goalInfo.total}
            </div>
          </div>

          {/* Progress Section */}
          <div style={{marginBottom: '20px'}}>
            {/* Completion Progress */}
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
              <span style={{fontSize: '14px', fontWeight: '500', color: '#555'}}>Completion</span>
              <span style={{fontSize: '16px', fontWeight: '600', color: getProgressColor(plan.progress?.percent || 0)}}>
                {Math.round(plan.progress?.percent || 0)}%
              </span>
            </div>
            <div style={{
              width: '100%',
              height: '12px',
              backgroundColor: '#e0e0e0',
              borderRadius: '6px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${plan.progress?.percent || 0}%`,
                height: '100%',
                backgroundColor: getProgressColor(plan.progress?.percent || 0),
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '12px', color: '#888'}}>
              <span>{plan.progress?.completed || 0} of {plan.progress?.total || 0} assignments completed</span>
              <span>{(plan.progress?.total || 0) - (plan.progress?.completed || 0)} remaining</span>
            </div>

            {/* Schedule Status */}
            {(() => {
              const scheduleStatus = getScheduleStatus(plan);
              const overdueCount = plan.progress?.due || 0;
              return (
                <div style={{
                  marginTop: '12px',
                  padding: '10px 12px',
                  backgroundColor: scheduleStatus.color === '#4caf50' ? '#e8f5e9' : scheduleStatus.color === '#ff9800' ? '#fff3e0' : '#ffebee',
                  borderLeft: `4px solid ${scheduleStatus.color}`,
                  borderRadius: '4px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    color: scheduleStatus.color,
                    fontWeight: '600'
                  }}>
                    {scheduleStatus.icon && <span style={{fontSize: '16px'}}>{scheduleStatus.icon}</span>}
                    <span>Schedule: {scheduleStatus.text}</span>
                  </div>
                  {overdueCount > 0 && (
                    <div style={{
                      marginTop: '4px',
                      fontSize: '12px',
                      color: '#666'
                    }}>
                      {overdueCount} overdue assignment{overdueCount !== 1 ? 's' : ''} (deadline passed)
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Timeline and Schedule Info */}
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px'}}>
            <div>
              <div style={{fontSize: '12px', color: '#999', marginBottom: '5px'}}>Start Date</div>
              <div style={{fontSize: '14px', fontWeight: '500', color: '#333'}}>{formatDate(plan.timeline.start)}</div>
            </div>
            <div>
              <div style={{fontSize: '12px', color: '#999', marginBottom: '5px'}}>End Date</div>
              <div style={{fontSize: '14px', fontWeight: '500', color: '#333'}}>{formatDate(plan.timeline.end)}</div>
            </div>
            <div>
              <div style={{fontSize: '12px', color: '#999', marginBottom: '5px'}}>Schedule</div>
              <div style={{fontSize: '14px', fontWeight: '500', color: '#333'}}>{plan.schedule.daysPerWeek} days/week</div>
            </div>
            <div>
              <div style={{fontSize: '12px', color: '#999', marginBottom: '5px'}}>Days</div>
              <div style={{fontSize: '14px', fontWeight: '500', color: '#333'}}>
                {plan.schedule.specificDays.map(d => d.substring(0, 3)).join(', ')}
              </div>
            </div>
          </div>

          {/* Notes */}
          {plan.notes && (
            <div style={{marginTop: '20px', padding: '12px', backgroundColor: '#fff3e0', borderRadius: '6px', borderLeft: '4px solid #ff9800'}}>
              <div style={{fontSize: '12px', fontWeight: '600', color: '#e65100', marginBottom: '5px'}}>NOTES</div>
              <div style={{fontSize: '13px', color: '#666', whiteSpace: 'pre-wrap'}}>{plan.notes}</div>
            </div>
          )}
        </div>
      </div>

      {/* Assignments List */}
      <div className="form-card">
        <div className="form-section">
          <h3 style={{marginBottom: '15px'}}>Assignments ({assignments.length})</h3>

          {assignments.length === 0 ? (
            <div style={{textAlign: 'center', padding: '40px', color: '#999'}}>
              <div style={{fontSize: '48px', marginBottom: '15px'}}>📝</div>
              <p>No assignments created yet for this learning plan.</p>
              <p style={{fontSize: '14px', marginTop: '10px'}}>
                Assignments will appear here once you generate and approve them from the plan creation flow.
              </p>
            </div>
          ) : (
            <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
              {assignments.map((assignment, index) => (
                <div
                  key={assignment._id}
                  style={{
                    padding: '15px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: assignment.assessment?.rating ? '#f1f8f4' : '#fafafa',
                    transition: 'box-shadow 0.2s',
                    cursor: 'pointer'
                  }}
                  onClick={() => navigate(`/assignments/${assignment._id}`)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start'}}>
                    <div style={{flex: 1}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px'}}>
                        <span style={{fontSize: '14px', fontWeight: '600', color: '#666'}}>#{index + 1}</span>
                        <span style={{fontSize: '15px', fontWeight: '500', color: '#333'}}>{assignment.title}</span>
                      </div>
                      <div style={{fontSize: '13px', color: '#666', marginBottom: '8px'}}>
                        {getAssignmentContent(assignment)}
                      </div>
                      <div style={{display: 'flex', alignItems: 'center', gap: '15px', fontSize: '12px', color: '#888'}}>
                        <span>
                          <strong>Due:</strong> {formatDate(assignment.dueDate)}
                        </span>
                        <span style={{textTransform: 'capitalize'}}>
                          <strong>Type:</strong> {assignment.type.replace('-', ' ')}
                        </span>
                      </div>
                    </div>
                    <div>
                      {getAssignmentStatusBadge(assignment)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Helper Text */}
      {assignments.length > 0 && (
        <div style={{
          marginTop: '20px',
          padding: '15px',
          backgroundColor: '#e3f2fd',
          borderRadius: '8px',
          fontSize: '14px',
          color: '#1565c0'
        }}>
          <strong>💡 Tip:</strong> Click on any assignment to view details or assess student progress. The plan automatically updates as assignments are assessed.
        </div>
      )}

      {/* Status Change Confirmation Modal */}
      {showStatusModal && (
        <div className="modal-overlay" style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000}}>
          <div className="modal-container" style={{backgroundColor: 'white', borderRadius: '12px', padding: '30px', maxWidth: '500px', width: '90%'}}>
            <h3 style={{marginBottom: '15px', color: pendingStatus === 'completed' || pendingStatus === 'cancelled' ? '#d32f2f' : '#333'}}>Confirm Status Change</h3>
            <p style={{marginBottom: '25px', color: '#666'}}>
              {pendingStatus === 'paused' && 'Are you sure you want to pause this learning plan? No further progress will be tracked until resumed.'}
              {pendingStatus === 'active' && 'Are you sure you want to resume this learning plan? Progress tracking will continue.'}
              {pendingStatus === 'completed' && (
                <>
                  <strong style={{color: '#d32f2f', display: 'block', marginBottom: '10px'}}>
                    ⚠️ WARNING: Marking as completed is permanent and cannot be undone.
                  </strong>
                  This indicates the student has successfully achieved the memorization goal. Proceed only if you are certain the goal has been met.
                </>
              )}
              {pendingStatus === 'cancelled' && (
                <>
                  <strong style={{color: '#d32f2f', display: 'block', marginBottom: '10px'}}>
                    ⚠️ WARNING: This action cannot be undone.
                  </strong>
                  Cancelling the plan will stop all progress tracking. The plan cannot be resumed after cancellation.
                </>
              )}
            </p>
            <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setPendingStatus(null);
                }}
                className="btn-secondary"
                style={{padding: '10px 20px'}}
              >
                Cancel
              </button>
              <button
                onClick={confirmStatusChange}
                className="btn-primary"
                style={{
                  padding: '10px 20px',
                  backgroundColor: pendingStatus === 'cancelled' ? '#f44336' : undefined
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000}}>
          <div className="modal-container" style={{backgroundColor: 'white', borderRadius: '12px', padding: '30px', maxWidth: '550px', width: '90%'}}>
            <h3 style={{marginBottom: '15px', color: '#d32f2f'}}>Delete Learning Plan</h3>

            {assignments.length > 0 ? (
              <>
                <p style={{marginBottom: '20px', color: '#666', fontWeight: '500'}}>
                  ⚠️ This plan has {assignments.length} assignment(s). What would you like to do?
                </p>

                <div style={{marginBottom: '25px', display: 'flex', flexDirection: 'column', gap: '15px'}}>
                  {/* Option 1: Keep assignments */}
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      padding: '15px',
                      border: `2px solid ${deleteAssignments ? '#ddd' : '#2196f3'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: deleteAssignments ? '#fff' : '#f5f9ff',
                      transition: 'all 0.2s'
                    }}
                  >
                    <input
                      type="radio"
                      name="deleteOption"
                      checked={!deleteAssignments}
                      onChange={() => setDeleteAssignments(false)}
                      style={{
                        marginTop: '3px',
                        marginRight: '12px',
                        cursor: 'pointer',
                        flexShrink: 0,
                        width: '16px',
                        height: '16px'
                      }}
                    />
                    <div style={{flex: 1, textAlign: 'left'}}>
                      <div style={{fontWeight: '600', color: '#333', marginBottom: '5px'}}>
                        Keep assignments (convert to manual)
                      </div>
                      <div style={{fontSize: '13px', color: '#666', lineHeight: '1.5'}}>
                        • Assignments will remain in student's list<br/>
                        • You can still assess them<br/>
                        • Plan link will be removed
                      </div>
                    </div>
                  </label>

                  {/* Option 2: Delete assignments */}
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      padding: '15px',
                      border: `2px solid ${deleteAssignments ? '#f44336' : '#ddd'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: deleteAssignments ? '#ffebee' : '#fff',
                      transition: 'all 0.2s'
                    }}
                  >
                    <input
                      type="radio"
                      name="deleteOption"
                      checked={deleteAssignments}
                      onChange={() => setDeleteAssignments(true)}
                      style={{
                        marginTop: '3px',
                        marginRight: '12px',
                        cursor: 'pointer',
                        flexShrink: 0,
                        width: '16px',
                        height: '16px'
                      }}
                    />
                    <div style={{flex: 1, textAlign: 'left'}}>
                      <div style={{fontWeight: '600', color: '#d32f2f', marginBottom: '5px'}}>
                        Delete plan AND all {assignments.length} assignment(s)
                      </div>
                      <div style={{fontSize: '13px', color: '#666', lineHeight: '1.5'}}>
                        • All assignments will be permanently deleted<br/>
                        • Students will no longer see them<br/>
                        • Assessments will be lost<br/>
                        • <strong style={{color: '#d32f2f'}}>THIS CANNOT BE UNDONE</strong>
                      </div>
                    </div>
                  </label>
                </div>
              </>
            ) : (
              <p style={{marginBottom: '25px', color: '#666'}}>
                This plan has no assignments. The plan will be deleted.
              </p>
            )}

            <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteAssignments(false); // Reset to default
                }}
                className="btn-secondary"
                style={{padding: '10px 20px'}}
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePlan}
                className="btn-danger"
                style={{
                  padding: '10px 20px',
                  backgroundColor: deleteAssignments ? '#d32f2f' : '#f44336',
                  color: 'white',
                  border: 'none'
                }}
              >
                {deleteAssignments ? 'Delete Plan & Assignments' : 'Delete Plan Only'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Learning Plan Modal */}
      {showEditModal && (
        <EditLearningPlanModal
          plan={plan}
          onClose={() => setShowEditModal(false)}
          onSuccess={(updatedPlan, message) => {
            setPlan(updatedPlan);
            setShowEditModal(false);
            setSuccessMessage(message);
            setTimeout(() => setSuccessMessage(''), 5000);
            // Refresh assignments list
            fetchPlanDetails();
          }}
        />
      )}
    </div>
  );
};

export default LearningPlanDetailsPage;
